import { createSignal, For, Show } from 'solid-js';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import { showDialog } from './Dialog';
import { formatDateTime } from '../services/dateFormat';
import styles from './PlayerNameEntryList.module.css';

export interface PlayerNameEntry {
    id: string;
    playerName: string;
    playerUuid?: string | null;
    reason?: string | null;
    note?: string | null;
    // Absent on draft entries that have not been saved yet.
    createdAt?: string | null;
}

export function EntryNoteButton(props: { playerName: string; note?: string | null; onSave: (note: string) => void | Promise<void> }) {
    const { t } = useTranslation();
    const edit = async () => {
        const result = await showDialog({
            title: t('PLAYER_ENTRY_NOTE_TITLE', props.playerName),
            fields: [{ key: 'note', label: t('PLAYER_ENTRY_NOTE'), type: 'text', defaultValue: props.note ?? '', placeholder: t('PLAYER_ENTRY_NOTE_PLACEHOLDER') }],
        });
        if (result == null) return;
        await props.onSave(result.note ?? '');
    };
    return (
        <button class="btn btn-icon btn-secondary btn-sm" title={t('PLAYER_ENTRY_NOTE_EDIT')} onClick={() => void edit()}>
            <i class="fa-solid fa-pen-to-square" />
        </button>
    );
}

// Names typed in by hand, next to PlayerListAttachPicker wherever a feature takes players both ways
// (PlotACL ban/whitelist, hologram stat exclusions).
export default function PlayerNameEntryList(props: {
    titleKey: string;
    helpKey: string;
    placeholderKey: string;
    emptyKey: string;
    icon: string;
    items: readonly PlayerNameEntry[];
    onAdd: (name: string) => void | Promise<void>;
    onRemove: (item: PlayerNameEntry) => void | Promise<void>;
    onNoteChange?: (item: PlayerNameEntry, note: string) => void | Promise<void>;
    reasonLabel?: (reason: string) => string;
    nested?: boolean;
}) {
    const { t } = useTranslation();
    const [name, setName] = createSignal('');
    const submit = async () => {
        const trimmed = name().trim();
        if (trimmed.length === 0) return;
        await props.onAdd(trimmed);
        setName('');
    };
    return (
        <div class={props.nested ? `v ${styles.nestedList}` : 'fcard v'}
            style={props.nested ? undefined : { gap: 'var(--spacing-md)' }}>
            <h2 class={props.nested ? styles.nestedTitle : styles.sectionTitle}><Text val={props.titleKey} /></h2>
            <span class={styles.hint}><Text val={props.helpKey} /></span>
            <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                <input type="text" style={{ flex: 1 }} value={name()} placeholder={t(props.placeholderKey)}
                    onInput={(e) => setName(e.currentTarget.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                <button class={`btn btn-secondary${props.nested ? ' btn-sm' : ''}`} title={t('PLAYER_ENTRY_ADD')}
                    onClick={() => void submit()}>
                    <i class="fa-solid fa-plus" />
                </button>
            </div>
            <Show when={props.items.length > 0} fallback={
                <div class={styles.emptySmall}><i class={`fa-solid ${props.icon}`} /><p><Text val={props.emptyKey} /></p></div>
            }>
                <div class={styles.feed}>
                    <For each={props.items}>{(item) => (
                        <div class={styles.entryRow}>
                            <div class={styles.entryMain}>
                                <span class={styles.entryName}>{item.playerName}</span>
                                <Show when={item.reason || item.playerUuid || item.createdAt}>
                                    <div class={styles.entryMeta}>
                                        <Show when={item.reason}>
                                            <span class={styles.badge}>
                                                {props.reasonLabel ? props.reasonLabel(item.reason!) : item.reason}
                                            </span>
                                        </Show>
                                        <Show when={item.playerUuid}><span class={styles.entryUuid}>{item.playerUuid}</span></Show>
                                        <Show when={item.createdAt}><span class={styles.entryTime}>{formatDateTime(item.createdAt!)}</span></Show>
                                    </div>
                                </Show>
                                <Show when={item.note}><span class={styles.entryNote}>{item.note}</span></Show>
                            </div>
                            <div class={styles.entryActions}>
                                <Show when={props.onNoteChange}>
                                    {(onNoteChange) => (
                                        <EntryNoteButton playerName={item.playerName} note={item.note}
                                            onSave={(note) => onNoteChange()(item, note)} />
                                    )}
                                </Show>
                                <button class="btn btn-icon btn-danger btn-sm" title={t('REMOVE')} onClick={() => void props.onRemove(item)}>
                                    <i class="fa-solid fa-trash" />
                                </button>
                            </div>
                        </div>
                    )}</For>
                </div>
            </Show>
        </div>
    );
}
